import type { FC } from "react";
import { AD_CSS_ISOLATION_CLASS } from "@hightouch/ad-studio-renderer/doc-renderer";

/**
 * CSS isolation for ads rendered live into the app DOM.
 *
 * The editor canvas (`DocRenderer`), every `AdAsset` thumbnail/preview, and the
 * inline Remotion player's html-overlay root inject ad markup straight into the page, where it inherits the app's global
 * stylesheet — most damagingly the CSS-Remedy reset in `global-styles.tsx`
 * (`img, video { max-width: 100% }`). Ad backgrounds are routinely sized WIDER
 * than the canvas and cropped via `object-fit: cover` + offset (the "zoom past
 * the margin" technique), so `max-width: 100%` silently clamps them to their
 * container and shifts the crop. The screenshot/export paths render inside an
 * isolated `<svg><foreignObject>` and are unaffected — which is why exports look
 * right but the live UI doesn't.
 *
 * Usage: put {@link AD_CSS_ISOLATION_CLASS} on the element that directly WRAPS
 * the injected ad markup, and render `<AdCssIsolation />` nearby (a sibling, so
 * the `<style>` itself isn't reset). The reset targets DESCENDANTS only
 * (`.class *`), so the wrapper keeps its own styles — important because that
 * wrapper is often a Chakra/emotion element whose styles come from a class, not
 * inline, and `all: revert` would otherwise wipe them. The ad markup's own
 * inline styles are higher priority than the reset, so they survive; we re-add
 * `box-sizing: border-box` because `all: revert` falls back to UA `content-box`.
 *
 * Mirrors `layers/utils.ts` FOREIGNOBJECT_CSS_ISOLATION + the capture-path
 * box-sizing reset.
 */
export { AD_CSS_ISOLATION_CLASS };

// SVG internals are excluded: `all: revert` wipes SVG *presentation
// attributes* too (they cascade as author-origin declarations), most fatally
// `d` on <path> — reverting it to `d: none` erases the path's geometry so the
// shape disappears entirely (e.g. white wave cutouts). Inline `style`
// attributes survive the revert, but geometry/paint set via attributes does
// not. The app's global resets target HTML elements (img/video), so SVG
// internals don't need the shield.
export const AD_CSS_ISOLATION_CSS = `.${AD_CSS_ISOLATION_CLASS} *:not(svg *),.${AD_CSS_ISOLATION_CLASS} *:not(svg *)::before,.${AD_CSS_ISOLATION_CLASS} *:not(svg *)::after{all:revert;box-sizing:border-box}`;

export const AdCssIsolation: FC = () => (
  // biome-ignore lint/security/noDangerouslySetInnerHtml: static, self-authored CSS string — no user input
  <style dangerouslySetInnerHTML={{ __html: AD_CSS_ISOLATION_CSS }} />
);
